import { datadogRum } from '@datadog/browser-rum';
import { cloneElement, type ErrorInfo, type ReactNode } from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import type { DatadogRumUser } from '@frontend/utils/datadogRumUtils';
import { logout } from '@frontend/store/authentication/actions';
import { FetchError } from '@frontend/store/fetchError';
import { TimeoutError } from '@frontend/store/timeoutError';
import { getSelectedLanguage } from '@frontend/translate';
import { getDatadogRumSessionId, getDatadogRumUser } from '@frontend/utils/datadogRumUtils';
import { startView } from '@frontend/utils/hooks/useDatadogView';
import type { IReduxState } from '../../configureStore';
import type { IConnectProps } from '../../utils/connectProps';
import type { IWithRouterProps } from './HoC/withRouter';
import { GeneralError } from '../pages/errors/GeneralError';
import { Component } from './Component';
import { withRouter } from './HoC/withRouter';

interface ICatchingComponentState {
    errorMessage?: string;
    hasError: boolean;
    rumSessionId?: string;
    rumUser?: DatadogRumUser;
    statusCode?: number;
}

interface ICatchingComponentProps extends IWithRouterProps, IConnectProps {
    children: ReactNode;
}

interface ICatchingComponentReduxProps {
    selectedEmploymentId: string;
}

const mapStateToProps = ({ authentication: { selectedEmploymentId } }: IReduxState): ICatchingComponentReduxProps => ({
    selectedEmploymentId,
});

let datadogErrorViewIsReported = false;
let logoutRedirectStarted = false;

/*
 * This component is a class component because it serves as an error boundary.
 * Error boundaries in React only work with class components.
 * They catch JavaScript errors anywhere in their child component tree,
 * log those errors, and display a fallback UI instead of the component tree that crashed.
 * Error boundaries catch errors during rendering, in lifecycle methods, and in constructors of the whole tree below them.
 */
class UnconnectedCatchingComponent extends Component<
    ICatchingComponentProps & ICatchingComponentReduxProps,
    ICatchingComponentState
> {
    private shouldShowUnauthorizedToast = false;

    constructor(props: ICatchingComponentProps & ICatchingComponentReduxProps) {
        super(props);
        this.state = { hasError: false };
    }

    public componentDidMount() {
        window.addEventListener('unhandledrejection', this.handleUnhandledRejection.bind(this));
    }

    public componentWillUnmount() {
        window.removeEventListener(
            'unhandledrejection',
            this.handleUnhandledRejection as EventListenerOrEventListenerObject
        );
    }

    public componentDidUpdate(prevProps: Readonly<ICatchingComponentProps & ICatchingComponentReduxProps>) {
        if (this.props.router.location !== prevProps.router.location) {
            this.setState({ hasError: false });
            datadogErrorViewIsReported = false;
        }

        // Show toast outside of render if needed
        if (this.shouldShowUnauthorizedToast) {
            this.shouldShowUnauthorizedToast = false;
            const translate = getSelectedLanguage();

            toast.error(`${translate.errorPages.unauthorizedAction}`, {
                autoClose: 5000,
                className: 'alert-danger',
                closeOnClick: true,
                draggable: true,
                hideProgressBar: false,
                onClose: () => {
                    // reset the error state
                    this.setState({
                        errorMessage: undefined,
                        hasError: false,
                        rumSessionId: undefined,
                        rumUser: undefined,
                        statusCode: undefined,
                    });
                },
                position: 'top-right',
                style: {
                    padding: '1rem',
                },
            });
        }
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        datadogRum.addError(error, { errorInfo });
        registerDatadogErrorView();

        const rumUser = getDatadogRumUser();
        const rumSessionId = getDatadogRumSessionId();

        const { message: errorMessage } = error;
        const statusCode = error instanceof FetchError ? error.apiErrorResponse?.statusCode : undefined;

        this.setState({
            errorMessage,
            hasError: true,
            rumSessionId,
            rumUser,
            statusCode,
        });
    }

    public render() {
        const { hasError, errorMessage, statusCode, rumUser, rumSessionId } = this.state;
        const { children } = this.props;

        const isUnauthorizedCrudOperation = errorMessage?.includes('403');

        switch (true) {
            // Show toast for authorization CRUD errors; block app with error page for general view errors.
            case isUnauthorizedCrudOperation:
                this.shouldShowUnauthorizedToast = true;

                return <>{children}</>;

            case hasError:
                return (
                    <GeneralError
                        errorMessage={errorMessage}
                        rumSessionId={rumSessionId}
                        rumUser={rumUser}
                        statusCode={statusCode}
                    />
                );

            case !!children:
                return cloneElement(children as React.ReactElement<unknown>, this.props);

            default:
                return null;
        }
    }

    private handleUnhandledRejection(promiseRejectionEvent: PromiseRejectionEvent) {
        if (logoutRedirectStarted) {
            // the window has already been redirected to the logout page. Error events might still come in but these should be ignored.
            return;
        }

        const { reason } = promiseRejectionEvent;

        promiseRejectionEvent.preventDefault();
        if (reason) {
            if (reason instanceof TimeoutError) {
                const redirectUrl = `${location.pathname}${location.search}`;
                logoutRedirectStarted = true;
                datadogRum.addAction('session-expired-redirect-to-logout');

                return logout(redirectUrl)(null);
            }
            const { name } = reason;

            if (name === 'AbortError') {
                return;
            }
        }

        registerDatadogErrorView();
        const rumUser = getDatadogRumUser();
        const rumSessionId = getDatadogRumSessionId();

        this.setState({
            errorMessage: reason?.message || '',
            hasError: true,
            rumSessionId,
            rumUser,
            statusCode: reason?.statusCode || '',
        });
    }
}

function registerDatadogErrorView() {
    if (datadogErrorViewIsReported === false) {
        datadogErrorViewIsReported = true;
        startView('error-page');
    }
}

export const CatchingComponent = connect(mapStateToProps)(withRouter(UnconnectedCatchingComponent));
